import React, { useState, useEffect } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line

// Assuming you have these icons available or replace with your SVG/components
// Example using feather-icons (you might need different icons based on the image)
// import { ReactComponent as SomeDecorativeIcon } from "path/to/your/icon.svg"; // For the red circle/dot

const SectionContainer = styled.div`
  ${tw`relative bg-gray-200 py-16 overflow-hidden`}
`;

const ContentWrapper = tw.div`
  container mx-auto px-6 sm:px-12 flex flex-col items-center gap-12
`;

const LeftPanel = styled.div`
  ${tw`relative lg:w-1/2 flex flex-col items-start z-10`}
`;

const RightPanel = styled.div`
  ${tw`relative lg:w-1/2 rounded-lg overflow-hidden z-10 shadow-lg h-[450px] md:h-[600px]`}
`;

const Tagline = tw.p`
  text-red-600 text-xs font-bold uppercase tracking-wider mb-2
`;

const Heading = tw.h2`
  text-4xl sm:text-5xl font-black text-gray-900 leading-tight pr-8
`;

const Description = tw.p`
  text-gray-600 mt-4 leading-relaxed lg:w-2/3
`;

const CalculatorCard = tw.div`
  bg-white rounded-lg shadow-lg p-8 mt-10 w-full max-w-md mx-auto lg:mx-0 z-10
`;

const SliderContainer = tw.div`
  mt-6
`;

const SliderLabel = tw.div`
  flex justify-between text-sm text-gray-700 font-semibold
`;

const SliderInputWrapper = styled.div`
  ${tw`relative w-full mt-3`}

  input[type="range"] {
    ${tw`w-full h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer`}

    /* Specific styling for the track fill */
    &::-webkit-slider-runnable-track {
      background: linear-gradient(
        to right,
        #ef4444 var(--fill-percent, 0%),
        #d1d5db var(--fill-percent, 0%)
      );
      height: 8px; /* Match slider height */
      border-radius: 8px;
    }

    &::-moz-range-track {
      background: linear-gradient(
        to right,
        #ef4444 var(--fill-percent, 0%),
        #d1d5db var(--fill-percent, 0%)
      );
      height: 8px; /* Match slider height */
      border-radius: 8px;
    }

    /* Thumb */
    &::-webkit-slider-thumb {
      ${tw`appearance-none w-5 h-5 bg-red-600 rounded-full border border-red-800 -mt-1`} /* Adjust margin to align with track */
      cursor: pointer;
    }

    &::-moz-range-thumb {
      ${tw`appearance-none w-5 h-5 bg-red-600 rounded-full border border-red-800`}
      cursor: pointer;
    }
  }
`;

const ValueDisplay = tw.div`
  flex justify-between items-center mt-6 pb-4 border-b border-gray-300 text-gray-700
`;

const ResultRow = tw.div`
  flex justify-between items-center py-4 border-b border-gray-300 text-gray-700
`;

const ResultLabel = tw.span`
  font-semibold
`;

const ResultValue = tw.span`
  font-bold text-gray-900
`;

const ApplyButton = tw.button`
  mt-8 w-full bg-red-600 text-white px-8 py-3 rounded hover:bg-red-700 transition duration-300 focus:outline-none focus:ring-2 focus:ring-red-500
`;

const Disclaimer = tw.p`
  text-xs text-gray-500 mt-4 text-center
`;

const MainImage = tw.img`
  w-full h-[450px] md:h-[600px] object-cover
`;

const SatisfactionBox = styled.div`
  ${tw`absolute bottom-0 left-0 bg-red-600 text-white p-6 font-bold shadow-xl`} /* Position bottom left, add shadow */
   transform: translate(20%, -20%) rotate(-5deg); /* Adjust position and rotation */
  min-width: 180px; /* Ensure it's wide enough */
  z-index: 10; /* Ensure it's above the image */

  /* Speech bubble tail */
  ::before {
    content: "";
    ${tw`absolute w-0 h-0`}
    border-left: 20px solid transparent; /* Controls the width and angle of the tail */
    border-right: 20px solid transparent; /* Controls the width and angle of the tail */
    border-top: 20px solid #dc2626; /* Red border matching background, creates the tail shape */
    bottom: -20px; /* Position below the box */
    left: 50%; /* Center the tail */
    transform: translateX(-50%); /* Adjust for centering */
  }
`;

const SatisfactionNumber = tw.span`
  text-5xl font-black leading-none
`;

const SatisfactionText = tw.span`
  text-sm mt-1 uppercase tracking-wider block text-center
`;

export default () => {
  // State for sliders and results
  const [loanAmount, setLoanAmount] = useState(100000); // Start near max as in image
  const [loanTerm, setLoanTerm] = useState(9); // Start at 9 months as in image
  const [payMonthly, setPayMonthly] = useState(0);
  const [totalPayBack, setTotalPayBack] = useState(0);

  // Define min/max for sliders
  const minLoan = 1000;
  const maxLoan = 1000000;
  const minTerm = 1;
  const maxTerm = 12;

  // Calculation logic based on the example values ($40k, 9 months -> $5111/month, $46000 total)
  // This suggests a total interest/fee of $6000 over 9 months on $40k.
  // Let's approximate a simple interest rate or scaling factor.
  // Total interest for $40000 over 9 months = $46000 - $40000 = $6000
  // Simple monthly interest factor relative to principal = $6000 / (40000 * 9) ≈ 0.0166667
  const monthlySimpleInterestFactor = 0.1; // Derived from the example

  useEffect(() => {
    // Calculate Total Pay Back using a simple interest approximation
    // Total Pay Back = Principal + (Principal * Monthly Simple Interest Factor * Term)
    const calculatedTotalPayBack =
      loanAmount + loanAmount * monthlySimpleInterestFactor * loanTerm;

    // Calculate Pay Monthly
    // Pay Monthly = Total Pay Back / Term
    const calculatedPayMonthly = calculatedTotalPayBack / loanTerm;

    setTotalPayBack(calculatedTotalPayBack);
    setPayMonthly(calculatedPayMonthly);
  }, [loanAmount, loanTerm]); // Recalculate when loanAmount or loanTerm changes

  // Helper to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "UGX",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Helper to format months
  const formatTerm = (months) => {
    return `${months} Month${months > 1 ? "s" : ""}`;
  };

  // Calculate fill percentage for sliders
  const loanFillPercent = ((loanAmount - minLoan) / (maxLoan - minLoan)) * 100;
  const termFillPercent = ((loanTerm - minTerm) / (maxTerm - minTerm)) * 100;

  return (
    <SectionContainer id="loan-calculator">
      <ContentWrapper>
        <div tw="flex flex-col sm:flex-row sm:justify-between">
          <div>
            <Tagline>II CALCULATE YOUR RATE II</Tagline>
            <Heading>How much do you need for a loan?</Heading>
          </div>
          <Description>
            In times of need and emergency, Wealthwise is here to rescue you
            with its low interest rates and favorable payment plans.
          </Description>
        </div>
        <div tw="mt-8 flex flex-col sm:flex-row items-center gap-y-6 sm:justify-between w-full">
          <LeftPanel>
            <CalculatorCard>
              {/* Loan Amount Slider */}
              <SliderContainer>
                <SliderLabel>
                  <span>{formatCurrency(minLoan)}</span>
                  <span>{formatCurrency(maxLoan)}</span>
                </SliderLabel>
                <SliderInputWrapper
                  style={{ "--fill-percent": `${loanFillPercent}%` }}
                >
                  {" "}
                  {/* Apply fill percentage */}
                  <input
                    type="range"
                    min={minLoan}
                    max={maxLoan}
                    step="100" // Adjust step as needed
                    value={loanAmount}
                    onChange={(e) => setLoanAmount(Number(e.target.value))}
                  />
                </SliderInputWrapper>
                {/* Current value displayed below slider */}
                <div tw="text-center mt-2 text-sm font-semibold text-gray-800">
                  {formatCurrency(loanAmount)}
                </div>
              </SliderContainer>

              {/* Loan Term Slider */}
              <SliderContainer>
                <SliderLabel>
                  <span>{formatTerm(minTerm)}</span>
                  <span>{formatTerm(maxTerm)}</span>
                </SliderLabel>
                <SliderInputWrapper
                  style={{ "--fill-percent": `${termFillPercent}%` }}
                >
                  <input
                    type="range"
                    min={minTerm}
                    max={maxTerm}
                    step="1"
                    value={loanTerm}
                    onChange={(e) => setLoanTerm(Number(e.target.value))}
                  />
                </SliderInputWrapper>
                {/* Current value displayed below slider */}
                <div tw="text-center mt-2 text-sm font-semibold text-gray-800">
                  {loanTerm}
                </div>
              </SliderContainer>

              {/* Calculated Results */}
              <ResultRow>
                <ResultLabel>Monthly Payment</ResultLabel>
                <ResultValue>{formatCurrency(payMonthly)}</ResultValue>
              </ResultRow>
              <ResultRow>
                <ResultLabel>Repayment Period</ResultLabel>
                <ResultValue>{formatTerm(loanTerm)}</ResultValue>
              </ResultRow>
              <ResultRow css={tw`border-b-0`}>
                {" "}
                {/* Remove bottom border for the last row */}
                <ResultLabel>Total Paid Back</ResultLabel>
                <ResultValue>{formatCurrency(totalPayBack)}</ResultValue>
              </ResultRow>

              <ApplyButton as="a" href="#apply-loan">
                Apply For Loan
              </ApplyButton>

              <Disclaimer>
                *This calculator provides an estimate for your planning but the
                final exact figures will be provided at our office.
              </Disclaimer>
            </CalculatorCard>
          </LeftPanel>

          <RightPanel>
            {/* Replace with your actual image path */}
            <MainImage
              src="/imgs/approved-loan-application.jpg"
              alt="Person using a laptop and credit card"
            />{" "}
            {/* Found a similar image */}
            <SatisfactionBox>
              <SatisfactionNumber>99%</SatisfactionNumber>
              <SatisfactionText>Satisfied Customers</SatisfactionText>
            </SatisfactionBox>
            {/* The two small cards with "200" are part of the background, hard to recreate perfectly */}
            {/* You could add them as absolutely positioned elements if needed, but they are minor */}
          </RightPanel>
        </div>
      </ContentWrapper>
      {/* Scroll to top button is likely a separate component */}
      {/* <ScrollToTopButton /> */}
    </SectionContainer>
  );
};
